import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { createHashHistory, createBrowserHistory } from "@tanstack/react-router";

import "@xterm/xterm/css/xterm.css";
import "./index.css";

import { isElectron } from "./env";
import { restoreDesktopRouteOnBoot } from "./lib/desktopRoutePersistence";
import { getRouter } from "./router";
import { APP_DISPLAY_NAME } from "./branding";
import { syncDocumentWindowControlsOverlayClass } from "./lib/windowControlsOverlay";
import { AppSentryErrorBoundary, captureWebError, initWebSentry } from "./sentry";
import { installVisualViewportHeightSync } from "./lib/visualViewport";
import { runCmmdEmbedHandoffBeforeBoot } from "./embed/cmmdEmbedHandoff";

export async function bootstrapWebApp(root: HTMLElement | null = document.getElementById("root")) {
  if (!root) {
    throw new Error("Forge web root element was not found.");
  }

  await runCmmdEmbedHandoffBeforeBoot();

  // Electron loads the app from a file-backed shell, so hash history avoids path resolution issues.
  if (isElectron) {
    restoreDesktopRouteOnBoot();
  }
  const history = isElectron ? createHashHistory() : createBrowserHistory();
  const router = getRouter(history);

  initWebSentry();

  installVisualViewportHeightSync({
    root: document.documentElement,
    viewport: window.visualViewport,
    scroller: window,
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    captureWebError(event.reason, { origin: "window.unhandledrejection" });
  });

  if (isElectron) {
    syncDocumentWindowControlsOverlayClass();
  }

  document.title = APP_DISPLAY_NAME;

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <AppSentryErrorBoundary>
        <RouterProvider router={router} />
      </AppSentryErrorBoundary>
    </React.StrictMode>,
  );
}

if (process.env.NODE_ENV !== "test") {
  void bootstrapWebApp();
}
